import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "react-bootstrap-icons";
import { Badge } from "antd";
import styles from "./SidebarAccordionItem.module.scss";

const SidebarAccordionItem = ({ item, currentPath }) => {
  // Check if any direct child or nested group child is active
  const isChildActive = (children) =>
    Array.isArray(children) &&
    children.some((child) => child.link === currentPath);

  const hasActiveDirectChild = isChildActive(item.children);
  const hasActiveGroupChild =
    Array.isArray(item.groups) &&
    item.groups.some((group) => isChildActive(group.children));

  const hasActiveChild = hasActiveDirectChild || hasActiveGroupChild;

  const [isOpen, setIsOpen] = useState(hasActiveChild);

  // Keep accordion open if currentPath moves into one of its children
  useEffect(() => {
    if (hasActiveChild) {
      setIsOpen(true);
    }
  }, [hasActiveChild]);

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className={styles.accordionItem} dir="rtl">
      {/* Main Accordion Header */}
      <div
        className={`${styles.header} ${
          isOpen || hasActiveChild ? styles.headerActive : ""
        }`}
        onClick={toggleOpen}
        role="button"
        tabIndex={0}
      >
        {/* Right side: Icon + Title + Badge */}
        <div className={styles.headerRight}>
          <span className={styles.icon}>{item.icon}</span>
          <span className={styles.title}>{item.title}</span>
          {item.badge != null && item.badge > 0 && (
            <Badge
              count={item.badge}
              overflowCount={999}
              className={styles.headerBadge}
            />
          )}
        </div>

        {/* Left side: Chevron Arrow */}
        <span
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
        >
          <ChevronLeft />
        </span>
      </div>

      {/* Accordion Content */}
      <div
        className={`${styles.collapseWrapper} ${
          isOpen ? styles.collapseOpen : ""
        }`}
      >
        <div className={styles.content}>
          {/* Direct Children Links */}
          {Array.isArray(item.children) &&
            item.children.map((child) => {
              const isActive = child.link === currentPath;
              return (
                <Link
                  key={child.id || child.link}
                  to={child.link}
                  className={`${styles.linkItem} ${
                    isActive ? styles.linkItemActive : ""
                  }`}
                >
                  <div className={styles.linkRight}>
                    <span className={styles.dot} />
                    <span className={styles.linkText}>{child.title}</span>
                  </div>
                  {child.count != null && child.count > 0 && (
                    <Badge
                      count={child.count}
                      overflowCount={999}
                      className={styles.itemBadge}
                    />
                  )}
                </Link>
              );
            })}

          {/* Nested Groups (e.g. for Users section) */}
          {Array.isArray(item.groups) &&
            item.groups.map((group) => (
              <SubGroup
                key={group.id}
                group={group}
                currentPath={currentPath}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

// Sub-accordion for nested groups (e.g. "لیست کاربر", "افزودن کاربر")
const SubGroup = ({ group, currentPath }) => {
  const hasActiveChild =
    Array.isArray(group.children) &&
    group.children.some((child) => child.link === currentPath);

  const [isOpen, setIsOpen] = useState(hasActiveChild);

  useEffect(() => {
    if (hasActiveChild) {
      setIsOpen(true);
    }
  }, [hasActiveChild]);

  return (
    <div className={styles.subGroup} dir="rtl">
      <div
        className={`${styles.subGroupHeader} ${
          isOpen || hasActiveChild ? styles.subGroupHeaderActive : ""
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
        role="button"
        tabIndex={0}
      >
        <span className={styles.subGroupTitle}>{group.title}</span>
        <span
          className={`${styles.subChevron} ${
            isOpen ? styles.subChevronOpen : ""
          }`}
        >
          <ChevronLeft />
        </span>
      </div>

      <div
        className={`${styles.collapseWrapper} ${
          isOpen ? styles.collapseOpen : ""
        }`}
      >
        <div className={styles.subGroupContent}>
          {group.children.map((child) => {
            const isActive = child.link === currentPath;
            return (
              <Link
                key={child.id || child.link}
                to={child.link}
                className={`${styles.linkItem} ${styles.nestedLink} ${
                  isActive ? styles.linkItemActive : ""
                }`}
              >
                <div className={styles.linkRight}>
                  <span className={styles.dot} />
                  <span className={styles.linkText}>{child.title}</span>
                </div>
                {child.count != null && child.count > 0 && (
                  <Badge count={child.count} className={styles.itemBadge} />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SidebarAccordionItem;
